import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { assets as seedAssets, holdings as seedHoldings, transactions as seedTransactions } from '../data/mockData'
import { holdingValue } from '../domain/finance'
import type { AccountProfile, Asset, Holding, Order, PortfolioPoint, Transaction } from '../domain/types'

interface ManagedAccount extends AccountProfile {
  holdings: Holding[]
  transactions: Transaction[]
  orders: Order[]
  snapshots: PortfolioPoint[]
}

interface AdminAccountPatch {
  firstName?: string
  lastName?: string
  phone?: string
  iban?: string
  walletBalance?: number
  cardBalance?: number
  active?: boolean
}

interface AppStateValue {
  balancesHidden: boolean
  toggleBalances: () => void
  watchlist: Set<string>
  toggleWatchlist: (id: string) => void
  cardFrozen: boolean
  toggleCardFrozen: () => void
  permissions: Record<'online' | 'contactless' | 'atm', boolean>
  togglePermission: (key: 'online' | 'contactless' | 'atm') => void
  toast: string | null
  notify: (message: string) => void
  accounts: ManagedAccount[]
  currentAccount: ManagedAccount
  currentAccountId: string
  setCurrentAccountId: (id: string) => void
  marketAssets: Asset[]
  marketStatus: 'live' | 'delayed' | 'refreshing'
  refreshQuotes: () => Promise<void>
  getPortfolioHistory: (period: string) => PortfolioPoint[]
  getAssetHistory: (assetId: string, period: string) => Promise<PortfolioPoint[]>
  executeOrder: (assetId: string, side: 'buy' | 'sell', amount: number) => Promise<{ ok: boolean; message: string }>
  transferFunds: (destination: string, destinationType: 'phone' | 'iban', amount: number, note: string) => Promise<{ ok: boolean; message: string }>
  updateAccount: (accountId: string, patch: AdminAccountPatch) => void
  addFunds: (accountId: string, amount: number) => void
  addActivity: (accountId: string, activity: Omit<Transaction, 'id'>) => void
  updateActivity: (accountId: string, activityId: string, patch: Partial<Transaction>) => void
  deleteActivity: (accountId: string, activityId: string) => void
}

const AppStateContext = createContext<AppStateValue | null>(null)
const STORAGE_VERSION = 3

function readStored<T>(key: string, fallback: T): T {
  try { const value = localStorage.getItem(key); return value ? JSON.parse(value) as T : fallback } catch { return fallback }
}

const accountSeeds: ManagedAccount[] = [
  {
    id: 'account-daniel', firstName: 'Daniel', lastName: 'Micallef', phone: '+356 7700 1234',
    iban: 'MT84 MALT 0110 0001 2345 6789 0123 456', walletBalance: 5142.30, cardBalance: 1032.08, active: true,
    holdings: seedHoldings, transactions: seedTransactions, orders: [], snapshots: []
  },
  {
    id: 'account-maya', firstName: 'Maya', lastName: 'Spiteri', phone: '+356 7900 8842',
    iban: 'MT31 MALT 0110 0009 8765 4321 0987 654', walletBalance: 2840.16, cardBalance: 442.90, active: true,
    holdings: [
      { assetId: 'vwce', quantity: 8.4, averageCost: 118.20 },
      { assetId: 'aapl', quantity: 4.2, averageCost: 168.50 },
      { assetId: 'btc', quantity: 0.012, averageCost: 82100 }
    ],
    transactions: seedTransactions.slice(0, 3).map((item, index) => ({ ...item, id: `maya-${index}` })),
    orders: [], snapshots: []
  }
]

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [balancesHidden, setBalancesHidden] = useState(() => readStored('finvia:hidden', false))
  const [watchlist, setWatchlist] = useState(() => new Set(readStored<string[]>('finvia:watchlist', ['aapl','nvda','tsla','btc'])))
  const [cardFrozen, setCardFrozen] = useState(() => readStored('finvia:frozen', false))
  const [permissions, setPermissions] = useState(() => readStored('finvia:permissions', { online: true, contactless: true, atm: false }))
  const [accounts, setAccounts] = useState<ManagedAccount[]>(() => {
    const stored = readStored<{ version: number; accounts: ManagedAccount[] } | null>('finvia:accounts', null)
    return stored?.version === STORAGE_VERSION ? stored.accounts : accountSeeds
  })
  const [currentAccountId, setCurrentAccountIdState] = useState(() => readStored('finvia:current-account', accountSeeds[0].id))
  const [marketAssets, setMarketAssets] = useState(seedAssets)
  const [marketStatus, setMarketStatus] = useState<'live' | 'delayed' | 'refreshing'>('delayed')
  const [toast, setToast] = useState<string | null>(null)

  const persistAccounts = useCallback((next: ManagedAccount[]) => {
    localStorage.setItem('finvia:accounts', JSON.stringify({ version: STORAGE_VERSION, accounts: next }))
    return next
  }, [])

  const notify = useCallback((message: string) => {
    setToast(message)
    window.setTimeout(() => setToast(null), 3200)
  }, [])

  const updateAccounts = useCallback((recipe: (current: ManagedAccount[]) => ManagedAccount[]) => {
    setAccounts((current) => persistAccounts(recipe(current)))
  }, [persistAccounts])

  const hydrateAccounts = useCallback(async (admin = window.location.pathname.startsWith('/administrator')) => {
    try {
      const response = await fetch(admin ? '/api/admin' : '/api/account', { credentials: 'include' })
      if (!response.ok) return
      const payload = await response.json() as { account?: ManagedAccount; accounts?: ManagedAccount[] }
      const next = payload.accounts ?? (payload.account ? [payload.account] : [])
      if (next.length) setAccounts(next)
    } catch { /* Local Vite preview has no Pages Functions runtime. */ }
  }, [])

  useEffect(() => {
    let ignore = false
    const hydrateIfAuthenticated = async () => {
      try {
        const response = await fetch('/api/auth/status', { credentials: 'include' })
        if (!response.ok) return
        const payload = await response.json() as { role?: 'user' | 'admin' | null }
        const adminRoute = window.location.pathname.startsWith('/administrator')
        if (!ignore && payload.role && (!adminRoute || payload.role === 'admin')) await hydrateAccounts(adminRoute)
      } catch { /* Pages Functions are unavailable in the local Vite runtime. */ }
    }
    void hydrateIfAuthenticated()
    const unlocked = () => void hydrateAccounts(window.location.pathname.startsWith('/administrator'))
    window.addEventListener('finvia:unlocked', unlocked)
    return () => { ignore = true; window.removeEventListener('finvia:unlocked', unlocked) }
  }, [hydrateAccounts])
  const currentAccount = accounts.find((account) => account.id === currentAccountId) ?? accounts[0]

  const setCurrentAccountId = useCallback((id: string) => {
    setCurrentAccountIdState(id)
    localStorage.setItem('finvia:current-account', JSON.stringify(id))
  }, [])

  const refreshQuotes = useCallback(async () => {
    setMarketStatus('refreshing')
    try {
      const response = await fetch('/api/market?symbols=' + seedAssets.map((asset) => asset.symbol).join(','))
      if (!response.ok) throw new Error('quote refresh failed')
      const payload = await response.json() as { quotes?: Array<{ symbol: string; price: number; change: number }>; complete?: boolean }
      if (!payload.quotes?.length) throw new Error('no quotes returned')
      const quoteMap = new Map(payload.quotes.map((quote) => [quote.symbol, quote]))
      setMarketAssets((current) => current.map((asset) => {
        const quote = quoteMap.get(asset.symbol)
        return quote ? { ...asset, price: quote.price, change: quote.change } : asset
      }))
      setMarketStatus(payload.complete ? 'live' : 'delayed')
      await hydrateAccounts(false)
    } catch {
      setMarketStatus('delayed')
    }
  }, [hydrateAccounts])

  useEffect(() => {
    let ignore = false
    const refreshIfAuthenticated = async () => {
      try {
        const response = await fetch('/api/auth/status', { credentials: 'include' })
        if (!response.ok) return
        const payload = await response.json() as { role?: 'user' | 'admin' | null }
        if (!ignore && payload.role) await refreshQuotes()
      } catch { /* Pages Functions are unavailable in the local Vite runtime. */ }
    }
    void refreshIfAuthenticated()
    const unlocked = () => void refreshQuotes()
    window.addEventListener('finvia:unlocked', unlocked)
    return () => { ignore = true; window.removeEventListener('finvia:unlocked', unlocked) }
  }, [refreshQuotes])

  const getPortfolioHistory = useCallback((period: string) => {
    void period
    const snapshots = currentAccount.snapshots
    if (snapshots.length > 1) return snapshots
    const length = Math.max(2, ...currentAccount.holdings.map((holding) => marketAssets.find((asset) => asset.id === holding.assetId)?.sparkline.length ?? 0))
    return Array.from({ length }, (_, index) => ({
      date: index === length - 1 ? 'Now' : `${index + 1}`,
      value: currentAccount.holdings.reduce((sum, holding) => {
        const asset = marketAssets.find((item) => item.id === holding.assetId)
        if (!asset) return sum
        const pointIndex = Math.round(index * (asset.sparkline.length - 1) / Math.max(1, length - 1))
        return sum + holding.quantity * (asset.sparkline[pointIndex] ?? asset.price)
      }, 0)
    }))
  }, [currentAccount, marketAssets])

  const getAssetHistory = useCallback(async (assetId: string, period: string) => {
    const asset = marketAssets.find((item) => item.id === assetId)
    if (!asset) return []
    try {
      const response = await fetch(`/api/market?history=${encodeURIComponent(asset.symbol)}&period=${encodeURIComponent(period)}`)
      if (!response.ok) throw new Error('history unavailable')
      const payload = await response.json() as { points?: PortfolioPoint[] }
      if (payload.points?.length) {
        setMarketAssets((current) => current.map((item) => item.id === assetId ? { ...item, sparkline: payload.points!.map((point) => point.value), price: payload.points!.at(-1)?.value ?? item.price } : item))
        return payload.points
      }
    } catch { /* Preserve the last known series. */ }
    return asset.sparkline.map((value, index) => ({ date: `${index + 1}`, value }))
  }, [marketAssets])
  const executeOrder = useCallback(async (assetId: string, side: 'buy' | 'sell', amount: number) => {
    if (!import.meta.env.DEV) {
      try {
        const response = await fetch('/api/orders', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assetId, side, amount }) })
        const payload = await response.json() as { account?: ManagedAccount; error?: string }
        if (!response.ok || !payload.account) return { ok: false, message: payload.error || 'Order could not be completed.' }
        setAccounts([payload.account])
        return { ok: true, message: `${side === 'buy' ? 'Buy' : 'Sell'} order completed.` }
      } catch { return { ok: false, message: 'Order could not be completed.' } }
    }    const asset = marketAssets.find((item) => item.id === assetId)
    if (!asset || !Number.isFinite(amount) || amount <= 0) return { ok: false, message: 'Enter a valid order amount.' }
    const quantity = amount / asset.price
    const existing = currentAccount.holdings.find((holding) => holding.assetId === assetId)
    if (side === 'buy' && currentAccount.walletBalance < amount) return { ok: false, message: 'Insufficient wallet balance.' }
    if (side === 'sell' && (!existing || existing.quantity < quantity)) return { ok: false, message: 'Order exceeds the available holding.' }

    const now = new Date()
    updateAccounts((current) => current.map((account) => {
      if (account.id !== currentAccount.id) return account
      let nextHoldings: Holding[]
      if (side === 'buy') {
        if (existing) {
          const totalCost = existing.quantity * existing.averageCost + amount
          const nextQuantity = existing.quantity + quantity
          nextHoldings = account.holdings.map((holding) => holding.assetId === assetId ? { ...holding, quantity: nextQuantity, averageCost: totalCost / nextQuantity } : holding)
        } else {
          nextHoldings = [...account.holdings, { assetId, quantity, averageCost: asset.price }]
        }
      } else {
        nextHoldings = account.holdings.map((holding) => holding.assetId === assetId ? { ...holding, quantity: holding.quantity - quantity } : holding).filter((holding) => holding.quantity > 0.0000001)
      }
      const walletBalance = account.walletBalance + (side === 'sell' ? amount : -amount)
      const order: Order = { id: crypto.randomUUID(), accountId: account.id, assetId, side, quantity, price: asset.price, total: amount, createdAt: now.toISOString() }
      const transaction: Transaction = {
        id: crypto.randomUUID(), title: `${side === 'buy' ? 'Bought' : 'Sold'} ${asset.symbol}`,
        subtitle: `${quantity.toFixed(asset.type === 'crypto' ? 6 : 3)} units · ${asset.price.toFixed(2)} EUR`,
        amount: side === 'buy' ? -amount : amount, date: now.toLocaleString('en-MT'), category: 'investment'
      }
      const investmentValue = nextHoldings.reduce((sum, holding) => sum + holdingValue(holding, marketAssets), 0)
      return { ...account, holdings: nextHoldings, walletBalance, orders: [order, ...account.orders], transactions: [transaction, ...account.transactions], snapshots: [...account.snapshots.slice(-19), { date: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), value: investmentValue }] }
    }))
    return { ok: true, message: `${side === 'buy' ? 'Buy' : 'Sell'} order completed at ${asset.price.toFixed(2)} EUR.` }
  }, [currentAccount, marketAssets, updateAccounts])

  const transferFunds = useCallback(async (destination: string, destinationType: 'phone' | 'iban', amount: number, note: string) => {
    if (!import.meta.env.DEV) {
      try {
        const response = await fetch('/api/transfers', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ destination, destinationType, amount, note }) })
        const payload = await response.json() as { account?: ManagedAccount; error?: string }
        if (!response.ok || !payload.account) return { ok: false, message: payload.error || 'Transfer could not be completed.' }
        setAccounts([payload.account])
        return { ok: true, message: 'Transfer completed.' }
      } catch { return { ok: false, message: 'Transfer could not be completed.' } }
    }    if (!destination.trim()) return { ok: false, message: `Enter a valid ${destinationType === 'phone' ? 'phone number' : 'IBAN'}.` }
    if (!Number.isFinite(amount) || amount <= 0) return { ok: false, message: 'Enter a valid transfer amount.' }
    if (currentAccount.walletBalance < amount) return { ok: false, message: 'Insufficient wallet balance.' }
    const now = new Date()
    updateAccounts((current) => current.map((account) => account.id === currentAccount.id ? {
      ...account,
      walletBalance: account.walletBalance - amount,
      transactions: [{
        id: crypto.randomUUID(), title: destinationType === 'phone' ? `Transfer to ${destination}` : 'IBAN transfer',
        subtitle: note || (destinationType === 'iban' ? destination.replace(/s/g, '').slice(-8) : destination),
        amount: -amount, date: now.toLocaleString('en-MT'), category: 'transfer'
      }, ...account.transactions]
    } : account))
    return { ok: true, message: 'Transfer completed.' }
  }, [currentAccount, updateAccounts])

  const postAdmin = useCallback(async (body: Record<string, unknown>) => {
    if (import.meta.env.DEV) return
    try {
      const response = await fetch('/api/admin', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      const payload = await response.json() as { accounts?: ManagedAccount[]; error?: string }
      if (!response.ok || !payload.accounts) throw new Error(payload.error || 'Administrator update failed.')
      setAccounts(payload.accounts)
    } catch (error) { notify(error instanceof Error ? error.message : 'Administrator update failed.') }
  }, [notify])

  const updateAccount = useCallback((accountId: string, patch: AdminAccountPatch) => {
    const account = accounts.find((item) => item.id === accountId)
    if (!account) return
    const next = { ...account, ...patch }
    updateAccounts((current) => current.map((item) => item.id === accountId ? next : item))
    void postAdmin({ action: 'updateAccount', accountId, patch: next })
  }, [accounts, postAdmin, updateAccounts])
  const addFunds = useCallback((accountId: string, amount: number) => {
    if (!Number.isFinite(amount) || amount <= 0) return
    updateAccounts((current) => current.map((account) => account.id === accountId ? { ...account, walletBalance: account.walletBalance + amount, transactions: [{ id: crypto.randomUUID(), title: 'Wallet funding', subtitle: 'Administrator adjustment', amount, date: new Date().toISOString(), category: 'income' }, ...account.transactions] } : account))
    void postAdmin({ action: 'addFunds', accountId, amount })
  }, [postAdmin, updateAccounts])
  const addActivity = useCallback((accountId: string, activity: Omit<Transaction, 'id'>) => {
    updateAccounts((current) => current.map((account) => account.id === accountId ? { ...account, transactions: [{ ...activity, id: crypto.randomUUID() }, ...account.transactions] } : account))
    void postAdmin({ action: 'addActivity', accountId, activity })
  }, [postAdmin, updateAccounts])
  const updateActivity = useCallback((accountId: string, activityId: string, patch: Partial<Transaction>) => {
    const account = accounts.find((item) => item.id === accountId), activity = account?.transactions.find((item) => item.id === activityId)
    if (!activity) return
    const next = { ...activity, ...patch, id: activity.id }
    updateAccounts((current) => current.map((item) => item.id === accountId ? { ...item, transactions: item.transactions.map((row) => row.id === activityId ? next : row) } : item))
    void postAdmin({ action: 'updateActivity', accountId, activityId, activity: next })
  }, [accounts, postAdmin, updateAccounts])
  const deleteActivity = useCallback((accountId: string, activityId: string) => {
    updateAccounts((current) => current.map((account) => account.id === accountId ? { ...account, transactions: account.transactions.filter((item) => item.id !== activityId) } : account))
    void postAdmin({ action: 'deleteActivity', accountId, activityId })
  }, [postAdmin, updateAccounts])
  const toggleBalances = useCallback(() => setBalancesHidden((value) => { localStorage.setItem('finvia:hidden', JSON.stringify(!value)); return !value }), [])
  const toggleWatchlist = useCallback((id: string) => setWatchlist((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); localStorage.setItem('finvia:watchlist', JSON.stringify([...next])); return next }), [])
  const toggleCardFrozen = useCallback(() => setCardFrozen((value) => { localStorage.setItem('finvia:frozen', JSON.stringify(!value)); return !value }), [])
  const togglePermission = useCallback((key: 'online' | 'contactless' | 'atm') => setPermissions((current) => { const next = { ...current, [key]: !current[key] }; localStorage.setItem('finvia:permissions', JSON.stringify(next)); return next }), [])

  const value = useMemo(() => ({
    balancesHidden, toggleBalances, watchlist, toggleWatchlist, cardFrozen, toggleCardFrozen, permissions, togglePermission, toast, notify,
    accounts, currentAccount, currentAccountId, setCurrentAccountId, marketAssets, marketStatus, refreshQuotes, getPortfolioHistory, getAssetHistory,
    executeOrder, transferFunds, updateAccount, addFunds, addActivity, updateActivity, deleteActivity
  }), [balancesHidden, toggleBalances, watchlist, toggleWatchlist, cardFrozen, toggleCardFrozen, permissions, togglePermission, toast, notify, accounts, currentAccount, currentAccountId, setCurrentAccountId, marketAssets, marketStatus, refreshQuotes, getPortfolioHistory, getAssetHistory, executeOrder, transferFunds, updateAccount, addFunds, addActivity, updateActivity, deleteActivity])

  return <AppStateContext value={value}>{children}</AppStateContext>
}

export function useAppState() {
  const value = use(AppStateContext)
  if (!value) throw new Error('useAppState must be used within AppStateProvider')
  return value
}









